import { useEffect, useMemo, useRef, useState } from 'react';
import { CSSObject, css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { onlyText } from 'react-children-utilities';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Button,
  Chip,
  PopoverMenu,
  SearchField,
  useDebouncedValue,
  useFormField,
  useOnClickOutside,
  usePopoverMenu,
} from '@frontend/design-system';
import { dropdownStyledPopover } from '../../styles';
import { CheckListSelector, CheckListSelectorProps } from './check-list-selector';

type CustomStyles = {
  chipDropdownFilter?: CSSObject;
  popoverMenuWrapper?: CSSObject;
  checkListWrapper?: CSSObject;
  applyButton?: CSSObject;
  topBar?: CSSObject;
};

type Props<T> = {
  customStyles?: CustomStyles;
  defaultValue?: T[];
  highlightEmptyOnOpen?: boolean;
  hideSearchBar?: boolean;
  iconName?: IconName;
  renderIcon?: (isFilterActive: boolean, isOpen?: boolean) => React.ReactNode;
  trackingId?: string;
} & CheckListSelectorProps<T> &
  (
    | {
        dropdownLikeStyle: true;
        dropdownStyleLabel: string;
      }
    | {
        dropdownLikeStyle?: false;
        dropdownStyleLabel?: undefined;
      }
  );

export function CheckListPopoverSelector<T>(props: Props<T>) {
  const {
    customStyles,
    defaultValue = [],
    disabled,
    dropdownLikeStyle,
    dropdownStyleLabel,
    highlightEmptyOnOpen = false,
    hideSearchBar = true,
    iconName,
    label,
    options,
    onChange,
    renderIcon,
    trackingId,
    value,
    ...rest
  } = props;
  const { t } = useTranslation('analytics');
  const { close, getMenuProps, getTriggerProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-start',
  });
  const { ref: filterTriggerRef, ...triggerProps } = getTriggerProps();
  const [selection, setSelection] = useState<T[]>(value || []);
  const searchField = useFormField({ type: 'text' });
  const debouncedSearchValue = useDebouncedValue(searchField.value);

  const outsideClickRef = useRef<HTMLDivElement>(null);
  const isFilterActive = !isEqual(defaultValue.sort(), value?.sort());

  const leftElement = useMemo(() => {
    if (renderIcon) {
      return renderIcon(isFilterActive, isOpen);
    }

    if (dropdownLikeStyle) {
      return <Icon className={isOpen ? 'dropdown-icon open' : 'dropdown-icon'} name={'caret-down-small'} />;
    }

    return iconName ? <Icon name={iconName} /> : null;
  }, [renderIcon, dropdownLikeStyle, iconName, isFilterActive, isOpen]);

  const totalOptionCount = useMemo(() => Object.keys(options ?? {}).length, [options]);
  const shouldShowSearch = !hideSearchBar && totalOptionCount > 2;

  const filteredOptions = useMemo(() => {
    if (!debouncedSearchValue) return options;

    const searchValue = debouncedSearchValue.trim().toLowerCase();

    return Object.fromEntries(
      Object.entries(options ?? {}).filter(([_, value]) => {
        const textValue = typeof value === 'string' ? value : onlyText(value);
        return textValue.toLowerCase().includes(searchValue);
      })
    );
  }, [options, debouncedSearchValue]);

  const handleApply = () => {
    onChange?.(selection as T[]);
    close();
  };

  const handleCancel = () => {
    setSelection(value || []);
    searchField.onChange({ name: 'search', value: '' });
    close();
  };

  useOnClickOutside({
    ref: outsideClickRef,
    handler: handleCancel,
  });

  useEffect(() => {
    setSelection(value || []);
  }, [value]);

  return (
    <div ref={filterTriggerRef}>
      <Chip.DropdownFilter
        {...triggerProps}
        css={{
          gap: theme.spacing(1),
          maxWidth: 'none',
          ...(highlightEmptyOnOpen && isOpen && !value?.length && { backgroundColor: theme.colors.neutral10 }),
          ...(dropdownLikeStyle && { ...dropdownStyledPopover(dropdownStyleLabel) }),
          ...customStyles?.chipDropdownFilter,
        }}
        filterIsActive={!dropdownLikeStyle && isFilterActive}
        leftElement={leftElement}
        onClick={open}
        trackingId={trackingId}
      >
        {label}
      </Chip.DropdownFilter>

      <PopoverMenu {...getMenuProps()}>
        <div css={[styles.checkListWrapper, customStyles?.popoverMenuWrapper]} ref={outsideClickRef}>
          {shouldShowSearch && (
            <SearchField
              {...searchField}
              name='search'
              placeholder={t('Search')}
              onKeyDown={(e) => {
                const navKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab'];
                if (!navKeys.includes(e.key)) e.stopPropagation();
              }}
              css={{ margin: theme.spacing(1, 2, 0) }}
            />
          )}
          <div css={[styles.flexHead, customStyles?.topBar]}>
            <Button onClick={setSelection.bind(null, Object.keys(options) as T[])} variant='tertiary'>
              {t('Select all')}
            </Button>
            <Button onClick={setSelection.bind(null, [])} variant='tertiary'>
              {t('Clear')}
            </Button>
          </div>

          <CheckListSelector
            {...rest}
            options={filteredOptions}
            disabled={disabled}
            label={label}
            onChange={setSelection}
            showLabel={false}
            trackingIdBase={trackingId}
            value={selection}
            css={[
              customStyles?.checkListWrapper,
              css`
                padding: ${theme.spacing(1)};
              `,
            ]}
          />
          <Button
            css={[styles.applyButton, customStyles?.applyButton]}
            disabled={disabled}
            onClick={handleApply}
            trackingId={`${trackingId}-apply-button`}
          >
            {t('Apply')}
          </Button>
        </div>
      </PopoverMenu>
    </div>
  );
}

const styles = {
  flexHead: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: ${theme.spacing(1)};
    min-width: 180px;

    button {
      color: ${theme.colors.text.interactive};
    }
  `,

  checkListWrapper: css`
    padding: ${theme.spacing(1, 1, 1)};
  `,

  applyButton: css`
    margin: ${theme.spacing(2, 1, 1)};
    width: calc(100% - ${theme.spacing(2)});
  `,
};
